import { Pencil, Plus, Trash2, TriangleAlert } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Template,
  InterviewTemplate,
  Question as BackendQuestion,
  TemplateQuestionInForm,
} from '../../types';
import {
  fetchInterviewTemplates,
  deleteInterviewTemplate,
} from '../../services/interviewTemplateService';

import { v4 as uuidv4 } from 'uuid';
import { Button } from '../Common/button';

interface TemplatesSectionProps {
  /**
   * Whether to show the "New Template" button at the top.
   * Defaults to true. Set to false if the parent component already has its own button.
   */
  showCreateButton?: boolean;
}

/**
 * Generic section component to list and manage templates.
 * Used both on the dedicated Templates page and inside organization pages.
 */
export const TemplatesSection: React.FC<TemplatesSectionProps> = ({ 
  showCreateButton = true 
}) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapBackendToFrontendTemplate = useCallback(
    (backendTpl: InterviewTemplate & { can_delete?: boolean }): Template => ({
      id: backendTpl.id,
      name: backendTpl.name,
      description: backendTpl.description || undefined,
      questions: backendTpl.questions.map(
        (q: BackendQuestion): TemplateQuestionInForm => ({
          id: uuidv4(),
          text: q.text,
          order: q.order,
        }),
      ),
      createdAt: (backendTpl as any).created_at || new Date().toISOString(),
      updatedAt: (backendTpl as any).updated_at || new Date().toISOString(),
      can_delete: backendTpl.can_delete ?? true,
    }),
    [],
  );

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const interviewTemplates = await fetchInterviewTemplates();
      const mappedTemplates = interviewTemplates.map(mapBackendToFrontendTemplate);
      setTemplates(mappedTemplates);
    } catch (err: any) {
      console.error('Erreur lors du chargement des templates:', err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          t('templatesSection.loadError'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [mapBackendToFrontendTemplate]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async (id: string, name: string) => {
    if (globalThis.confirm(t('templatesSection.deleteConfirm', { name }))) {
      setIsLoading(true);
      setError(null);
      try {
        await deleteInterviewTemplate(id);
        await loadTemplates();
      } catch (err: any) {
        console.error('Erreur lors de la suppression du template:', err);
        setError(
          err.response?.data?.detail ||
            err.message ||
            t('templatesSection.deleteError'),
        );
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Bouton pour créer un nouveau template */}
      {showCreateButton && (
        <div className="flex justify-end">
          <Button asChild>
            <Link to="/templates/new">
              <Plus className="h-4 w-4 mr-2" />
              {t('templatesSection.newTemplate')}
            </Link>
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-4 text-sm text-muted-foreground">
          {t('templatesSection.loading')}
        </div>
      )}

      {error && (
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded"
          role="alert"
        >
          <div className="flex items-center mb-1">
            <TriangleAlert className="h-5 w-5 mr-2" />
            <p className="font-bold text-sm">{t('templatesSection.error')}</p>
          </div>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {!isLoading && !error && templates.length === 0 && (
        <div className="text-center text-muted-foreground py-6 text-sm">
          {t('templatesSection.noTemplates')}
        </div>
      )}

      {!isLoading && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="rounded-lg border border-border bg-card overflow-hidden flex flex-col"
            >
              <div className="p-4 flex-grow">
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  {template.name}
                </h3>
                {template.description && (
                  <p className="text-xs text-muted-foreground mb-3 line-clamp-3">
                    {template.description}
                  </p>
                )}
                {template.questions && template.questions.length > 0 && (
                  <div className="mt-auto pt-1">
                    <h4 className="text-[10px] font-medium text-muted-foreground uppercase mb-1">
                      {t('templatesSection.questions')}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {t('templatesSection.guidedQuestionsCount', { count: template.questions.length })}
                    </p>
                  </div>
                )}
              </div>
              <div className="bg-muted/50 px-4 py-2.5 border-t border-border flex flex-col gap-2">
                {template.can_delete === false && (
                  <p className="text-xs text-red-600 dark:text-red-400 break-words whitespace-normal min-w-0">
                    {t('layout.pages.templates.cannotDeleteBecauseUsed', 'Cannot be deleted because it is used')}
                  </p>
                )}
                <div className="flex justify-end space-x-2">
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/templates/edit/${template.id}`}>
                      <Pencil className="h-3 w-3 mr-1.5" />
                      {t('templatesSection.modify')}
                    </Link>
                  </Button>
                  <Button
                    onClick={() => handleDelete(template.id, template.name)}
                    size="sm"
                    variant="destructive"
                    disabled={isLoading || template.can_delete === false}
                  >
                    <Trash2 className="h-3 w-3 mr-1.5" />
                    {t('templatesSection.delete')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatesSection;


